/* global globalThis */
import { useState, useRef, useEffect, useMemo, useId } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import {
  Checkbox,
  InputAdornment,
  TextField,
  Radio,
  Tooltip,
  Typography,
} from "@mui/material";
import AccessibleSpinner from "../../common/AccessibleSpinner";
import SearchIcon from "../../../assets/search.svg";
import { capitalizeFirstLetter } from "../../../utils/capitalize";

const LABEL_FORMS = {
  Members: { singular: "member", plural: "members" },
  Providers: { singular: "provider", plural: "providers" },
  Pharmacies: { singular: "pharmacy", plural: "pharmacies" },
  Programs: { singular: "program", plural: "programs" },
  Disposition: { singular: "disposition", plural: "dispositions" },
  Assignee: { singular: "assignee", plural: "assignees" },
  Carrier: { singular: "carrier", plural: "carriers" },
  Groups: { singular: "group", plural: "groups" },
};

const computeDisplayValue = (label, placeholder, value, multiple) => {
  if (!multiple) return value || "";
  const selected = Array.isArray(value) ? value : [];
  const count = selected.length;
  if (count === 0) return placeholder;
  const forms = LABEL_FORMS[label] || {
    singular: label.endsWith("s")
      ? label.slice(0, -1).toLowerCase()
      : label.toLowerCase(),
    plural: label.toLowerCase() + (label.endsWith("s") ? "" : "s"),
  };
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  return count === 1
    ? `${count} ${cap(forms.singular)} Selected`
    : `${count} ${cap(forms.plural)} Selected`;
};

const filterOptions = (options, searchTerm, searchResults) => {
  if (searchTerm && searchResults.length > 0) return searchResults;
  if (searchTerm)
    return options.filter((o) =>
      o.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  return options;
};

const useOutsideClose = (
  refs,
  isOpen,
  searchTerm,
  setInputValue,
  setIsOpen,
) => {
  useEffect(() => {
    const nodes = Array.isArray(refs) ? refs : [refs];
    const handler = (e) => {
      const clickedInside = nodes.some((r) => r?.current?.contains(e.target));
      if (clickedInside) return;
      if (isOpen && !searchTerm) setInputValue("");
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [isOpen, searchTerm, refs, setInputValue, setIsOpen]);
};

const SelectAllCheckbox = ({ label, value, options, onSelectAll }) => {
  const total = options.length;
  const selectedCount = Array.isArray(value) ? value.length : 0;
  const allSelected = total > 0 && selectedCount === total;
  const indeterminate = selectedCount > 0 && selectedCount < total;
  return (
    <button
      type="button"
      className="dropdown-option select-all"
      style={{
        display: "flex",
        alignItems: "center",
        padding: "6px 8px",
        cursor: "pointer",
        marginBottom: "4px",
        borderRadius: "4px",
        backgroundColor: "#403c6f",
        minHeight: "28px",
        boxSizing: "border-box",
        border: "none",
        width: "100%",
        textAlign: "left",
      }}
      onClick={onSelectAll}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelectAll();
        }
      }}
      aria-label={`Select all ${label}`}
    >
      <span
        style={{
          color: "#fff",
          fontSize: "12px",
          fontWeight: 500,
          flexGrow: 1,
        }}
      >
        All
      </span>
      <Checkbox
        checked={allSelected}
        indeterminate={indeterminate}
        size="small"
        onChange={(e) => {
          e.stopPropagation();
          onSelectAll();
        }}
        slotProps={{
          input: {
            id: `filter-${label.toLowerCase()}-all-checkbox`,
            "aria-label": `${label} - All`,
          },
        }}
        style={{ padding: "2px", marginLeft: "6px", color: "#fff" }}
      />
    </button>
  );
};

SelectAllCheckbox.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectAll: PropTypes.func.isRequired,
};

const OptionRow = ({
  option,
  index,
  multiple,
  isSelected,
  label,
  onToggle,
  showRadio = false,
}) => {
  const ariaSelected = !multiple && isSelected ? "true" : undefined;
  const Component = multiple ? "label" : "button";
  const buttonProps = multiple
    ? {}
    : {
        type: "button",
        onClick: (e) => {
          e.stopPropagation();
          onToggle(option);
        },
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle(option);
          }
        },
      };

  return (
    <Component
      {...buttonProps}
      className={`dropdown-option ${isSelected ? "selected" : ""} ${
        index < 99999 ? "with-underline" : ""
      }`}
      style={{
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        padding: "4px 8px",
        minHeight: 28,
        border: "none",
        background: "transparent",
        width: "100%",
        textAlign: "left",
      }}
      role={multiple ? undefined : "option"}
      aria-selected={ariaSelected}
    >
      <Tooltip
        title={option}
        placement="bottom-start"
        arrow
        describeChild
        disableInteractive
        slotProps={{
          popper: {
            sx: {
              "& .MuiTooltip-tooltip": {
                backgroundColor: "#343435",
                color: "#FFFFFF",
                margin: 0,
              },
              "& .MuiTooltip-arrow": {
                color: "#343435",
                margin: 0,
                left: "50% !important",
                transform: "translateX(-50%) !important",
                "&::before": {
                  boxShadow: "none",
                },
              },
            },
          },
          tooltip: {
            sx: {
              margin: 0,
              marginTop: "0px !important",
              padding: "4px 8px",
            },
          },
        }}
      >
        <span
          className="option-text"
          style={{
            cursor: "pointer",
            flexGrow: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "12px",
          }}
          aria-label={option}
        >
          {label === "Program" ||
          label === "Programs" ||
          label === "Recent Activity" ||
          label === "Filter by Recent Activity"
            ? option
            : capitalizeFirstLetter(option)}
        </span>
      </Tooltip>
      {multiple && (
        <Checkbox
          checked={isSelected}
          size="small"
          onChange={(e) => {
            e.stopPropagation();
            onToggle(option);
          }}
          slotProps={{
            input: {
              id: `filter-${label.toLowerCase()}-checkbox-${index}`,
              "aria-label": option,
            },
          }}
          style={{ padding: "4px", marginLeft: "8px" }}
        />
      )}
      {showRadio && !multiple && (
        <Radio
          checked={isSelected}
          size="small"
          // Purely visual here - the wrapping <button> already calls
          // onToggle for any click inside the row, including on this radio.
          // A native radio input also fires its own "change" event on click,
          // which bubbles independently of (and isn't stopped by) the
          // button's click handler - wiring onToggle here too used to fire
          // it twice per click (e.g. two identical PATCH .../assignee calls).
          onChange={(e) => e.stopPropagation()}
          slotProps={{
            input: {
              id: `filter-${label.toLowerCase()}-radio-${index}`,
              "aria-label": option,
            },
          }}
          style={{ padding: "4px", marginLeft: "8px", color: "#fff" }}
        />
      )}
    </Component>
  );
};

OptionRow.propTypes = {
  option: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  multiple: PropTypes.bool,
  isSelected: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  showRadio: PropTypes.bool,
};

const getSearchPlaceholder = (label) => {
  const map = {
    Members: "Search member",
    Member: "Search member",
    Providers: "Search provider",
    Provider: "Search provider",
    Pharmacies: "Search pharmacy",
    Pharmacy: "Search pharmacy",
    Programs: "Search program",
    Program: "Search program",
  };
  return map[label] || "Search...";
};

const useFilterSelect = ({
  multiple,
  options,
  value,
  onChange,
  onSearch,
  onOpen,
  onLoadMore,
  isLoading = false,
  hasMore = false,
  filterType,
  autoPrefetch = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const dropdownRef = useRef(null);
  const optionsRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const loadingMoreRef = useRef(false);

  useOutsideClose(
    [dropdownRef, dropdownMenuRef],
    isOpen,
    searchTerm,
    setInputValue,
    setIsOpen,
  );

  const currentValues = multiple && Array.isArray(value) ? value : [];
  const clearAll = () => {
    multiple ? onChange?.([]) : onChange?.(null);
    setSearchTerm("");
    setInputValue("");
  };
  const toggleDropdown = (e) => {
    if (e?.target.closest(".dropdown-icon")) clearAll();
    if (isOpen && !searchTerm) setInputValue("");
    setIsOpen((o) => {
      const next = !o;
      if (next) {
        onOpen?.(filterType);
      }
      return next;
    });
  };
  const selectAll = () => {
    const allOptions = [...options];
    const shouldSelectAll = currentValues.length !== allOptions.length;
    onChange?.(shouldSelectAll ? allOptions : []);
  };
  const commitSearch = () => {
    const term = inputValue.trim();
    if (term) {
      setIsSearching(true);
      setSearchTerm(term);
      onSearch?.(term, filterType);
      setTimeout(() => setIsSearching(false), 300);
    } else {
      setSearchTerm("");
      setSearchResults([]);
      onSearch?.("", filterType);
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitSearch();
    }
  };
  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    if (!val.trim()) {
      setSearchTerm("");
      setSearchResults([]);
      onSearch?.("", filterType);
    }
  };
  const handleOptionClick = (option) => {
    if (!multiple) {
      onChange?.(option);
      setIsOpen(false);
      return;
    }
    const newValues = currentValues.includes(option)
      ? currentValues.filter((v) => v !== option)
      : [...currentValues, option];
    onChange?.(newValues);
    if (globalThis.innerWidth <= 768) setIsOpen(false);
  };
  const filteredOptions = useMemo(
    () => filterOptions(options, searchTerm, searchResults),
    [options, searchTerm, searchResults],
  );

  useEffect(() => {
    if (!isOpen) return;
    const menuEl = dropdownMenuRef.current;
    const listEl = optionsRef.current;
    if (!menuEl && !listEl) return;
    const userScrolledRef = { current: false };
    const onScroll = (el) => {
      if (loadingMoreRef.current) return;
      if (isSearching || isLoading) return;
      if (!hasMore) return;
      if (el.scrollTop > 0) userScrolledRef.current = true;
      const threshold = 0;
      if (
        userScrolledRef.current &&
        el.scrollTop + el.clientHeight >= el.scrollHeight - threshold
      ) {
        loadingMoreRef.current = true;
        void Promise.resolve(onLoadMore?.(filterType)).finally(() => {
          loadingMoreRef.current = false;
        });
      }
    };
    const menuHandler = () => onScroll(menuEl);
    const listHandler = () => onScroll(listEl);
    if (menuEl)
      menuEl.addEventListener("scroll", menuHandler, { passive: true });
    if (listEl)
      listEl.addEventListener("scroll", listHandler, { passive: true });
    return () => {
      if (menuEl) menuEl.removeEventListener("scroll", menuHandler);
      if (listEl) listEl.removeEventListener("scroll", listHandler);
    };
  }, [isOpen, isSearching, isLoading, hasMore, onLoadMore, filterType]);

  useEffect(() => {
    if (!autoPrefetch) return;
    if (!isOpen) return;
    const menuEl = dropdownMenuRef.current;
    const listEl = optionsRef.current;
    if (!menuEl && !listEl) return;
    if (loadingMoreRef.current) return;
    if (isLoading) return;
    if (!hasMore) return;
    const menuNeeds = menuEl
      ? menuEl.scrollHeight <= menuEl.clientHeight
      : false;
    const listNeeds = listEl
      ? listEl.scrollHeight <= listEl.clientHeight
      : false;
    if (menuNeeds || listNeeds) {
      loadingMoreRef.current = true;
      void Promise.resolve(onLoadMore?.(filterType)).finally(() => {
        loadingMoreRef.current = false;
      });
    }
  }, [
    autoPrefetch,
    isOpen,
    hasMore,
    isLoading,
    onLoadMore,
    filterType,
    filteredOptions.length,
  ]);
  return {
    dropdownRef,
    optionsRef,
    dropdownMenuRef,
    isOpen,
    toggleDropdown,
    inputValue,
    handleInputChange,
    handleKeyDown,
    commitSearch,
    isSearching,
    isLoading,
    filteredOptions,
    handleOptionClick,
    selectAll,
    hasMore,
  };
};

const FilterSelect = ({
  label,
  placeholder,
  value,
  multiple,
  options,
  onChange,
  onSearch,
  onOpen,
  onLoadMore,
  isLoading,
  hasMore,
  filterType,
  autoPrefetch = false,
  showLabel = true,

  showRadio = false,
  instanceId,
}) => {
  const {
    dropdownRef,
    optionsRef,
    dropdownMenuRef,
    isOpen,
    toggleDropdown,
    inputValue,
    handleInputChange,
    handleKeyDown,
    commitSearch,
    isSearching,
    isLoading: isLoadingFromHook,
    filteredOptions,
    handleOptionClick,
    selectAll,
  } = useFilterSelect({
    multiple,
    options,
    value,
    onChange,
    onSearch,
    onOpen,
    onLoadMore,
    isLoading,
    hasMore,
    filterType,
    autoPrefetch,
  });

  const [menuPos, setMenuPos] = useState({ left: 0, top: 0, width: 0 });
  const [menuPlacement, setMenuPlacement] = useState("down");

  useEffect(() => {
    if (!isOpen) return;
    const container = dropdownRef.current;
    const menuEl = dropdownMenuRef.current;
    if (!container) return;

    const width = 160;
    const computeAndSetPosition = () => {
      const rect = container.getBoundingClientRect();
      const viewportHeight =
        globalThis.innerHeight || document.documentElement.clientHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const desiredPlacement =
        spaceBelow < 200 && spaceAbove > spaceBelow ? "up" : "down";
      setMenuPlacement(desiredPlacement);

      const menuHeight = menuEl?.offsetHeight || 320;
      const gap = 4;
      const top =
        desiredPlacement === "up"
          ? rect.top - menuHeight - gap
          : rect.bottom + gap;
      setMenuPos({ left: rect.left, top, width });
    };

    requestAnimationFrame(computeAndSetPosition);

    const handleReposition = () => {
      computeAndSetPosition();
    };
    globalThis.addEventListener("scroll", handleReposition, true);
    globalThis.addEventListener("resize", handleReposition, true);
    return () => {
      globalThis.removeEventListener("scroll", handleReposition, true);
      globalThis.removeEventListener("resize", handleReposition, true);
    };
  }, [isOpen, filteredOptions.length, dropdownRef, dropdownMenuRef]);

  const displayValue = useMemo(
    () => computeDisplayValue(label, placeholder, value, multiple),
    [label, placeholder, value, multiple],
  );
  const searchPlaceholder = getSearchPlaceholder(label);

  useEffect(() => {
    const handler = (e) => {
      const sourceId = e?.detail?.sourceId;
      if (!sourceId) return;
      if (sourceId !== instanceId) {
        if (isOpen) {
          toggleDropdown();
        }
      }
    };
    globalThis.addEventListener("halo-close-other-filter-dropdowns", handler);
    return () =>
      globalThis.removeEventListener(
        "halo-close-other-filter-dropdowns",
        handler,
      );
  }, [isOpen, instanceId, toggleDropdown]);

  const uidHook = useId();
  const uid = instanceId || uidHook;
  const idBase = `filter-${label.toLowerCase()}-${uid}`;
  const displayId = `${idBase}-input-display`;
  const menuId = `${idBase}-menu`;
  const labelId = `${idBase}-label`;

  return (
    <div className="common-filter-input" ref={dropdownRef}>
      {showLabel && (
        <label className="filter-label" htmlFor={displayId} id={labelId}>
          {label}
        </label>
      )}
      <div className="filter-input-containers" style={{ position: "relative" }}>
        <button
          id={`filter-${label.toLowerCase()}-input-display`}
          className="filter-input"
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={`filter-${label.toLowerCase()}-menu`}
          aria-label={`${label}: ${displayValue}`}
          onClick={(e) => {
            e.stopPropagation();
            toggleDropdown(e);
            if (!isOpen) {
              // broadcast that this instance opened
              globalThis.dispatchEvent(
                new CustomEvent("halo-close-other-filter-dropdowns", {
                  detail: { sourceId: instanceId || `${label}` },
                }),
              );
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              toggleDropdown(e);
            }
          }}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            paddingRight: "12px",
            fontSize: "12px",
            fontWeight: 500,
            color: "#4a4a68",
            lineHeight: "150%",
            letterSpacing: "0.03em",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textTransform: "none",
          }}
        >
          <Typography
            noWrap
            sx={{
              textOverflow: "ellipsis",
              maxWidth: "110px",
              fontSize: "12px",
              fontWeight: 500,
              fontFamily:
                "Inter, ui-sans-serif, system-ui, sans-serif !important",
            }}
          >
            {displayValue}
          </Typography>
        </button>
        {isOpen ? (
          <button
            type="button"
            className="dropdown-icon"
            style={{
              cursor: "pointer",
              fontSize: "20px",
              color: "#666",
              pointerEvents: "auto",
              border: "none",
              background: "transparent",
              padding: 0,
              margin: 0,
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggleDropdown(e);
            }}
            aria-label="Close menu"
          >
            <CloseIcon
              style={{
                pointerEvents: "none",
                paddingBottom: "7px",
              }}
            />
          </button>
        ) : (
          <button
            type="button"
            className="dropdown-icon-button"
            aria-label={`Open ${label} menu`}
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown(e);
              if (!isOpen) {
                globalThis.dispatchEvent(
                  new CustomEvent("halo-close-other-filter-dropdowns", {
                    detail: { sourceId: instanceId || `${label}` },
                  }),
                );
              }
            }}
            style={{
              border: "none",
              background: "transparent",
              padding: 0,
              margin: 0,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ExpandMoreIcon
              className="dropdown-icon"
              aria-hidden="true"
              viewBox="0 0 24 24"
              style={{
                transition: "transform 0.2s",
                cursor: "pointer",
                pointerEvents: "none",
              }}
            />
          </button>
        )}
        {isOpen &&
          ReactDOM.createPortal(
            <section
              id={menuId}
              className={`dropdown-menu-single ${
                multiple ? "multiple" : ""
              } placement-${menuPlacement}`}
              aria-label={`${label} filter options`}
              ref={dropdownMenuRef}
              data-placement={menuPlacement}
              style={{
                position: "fixed",
                top: `${menuPos.top}px`,
                left: `${menuPos.left}px`,
                width: `${menuPos.width}px`,
                zIndex: 10000,
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                maxHeight: 200,
                overflow: "auto",

                lineHeight: 1.3,
              }}
            >
              <div className="options-container" ref={optionsRef}>
                <div className="search-option">
                  <TextField
                    type="text"
                    placeholder={searchPlaceholder}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    className="search-option-input"
                    size="small"
                    sx={{
                      "& .MuiInputBase-root": {
                        fontSize: "12px",
                        minHeight: 27,
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "6px 8px",
                        fontSize: "12px",
                      },
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#92A0B3",
                        },
                        minHeight: 27,
                      },
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {isSearching ? (
                              <AccessibleSpinner size={16} label="Searching" />
                            ) : (
                              <button
                                type="button"
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  padding: 0,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  marginRight: "6px",
                                }}
                                onClick={commitSearch}
                                aria-label="Execute search"
                              >
                                <img
                                  src={SearchIcon}
                                  alt="Search"
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                  }}
                                />
                              </button>
                            )}
                          </InputAdornment>
                        ),
                        style: { paddingRight: "6px" },
                        sx: {
                          "&:focus": { outline: "none", boxShadow: "none" },
                        },
                      },
                    }}
                  />
                </div>
                {multiple && (
                  <button
                    type="button"
                    className="dropdown-option select-all"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "6px 8px",
                      cursor: "pointer",
                      marginBottom: "4px",
                      borderRadius: "4px",
                      backgroundColor: "#403c6f",
                      minHeight: "28px",
                      boxSizing: "border-box",
                      border: "none",
                      width: "100%",
                      textAlign: "left",
                    }}
                    onClick={selectAll}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        selectAll();
                      }
                    }}
                    aria-label={`Select all ${label}`}
                  >
                    <span
                      style={{
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: 500,
                        flexGrow: 1,
                      }}
                    >
                      All
                    </span>
                    <Checkbox
                      checked={
                        multiple && Array.isArray(value)
                          ? value.length === options.length
                          : false
                      }
                      indeterminate={
                        multiple && Array.isArray(value)
                          ? value.length > 0 && value.length < options.length
                          : false
                      }
                      size="small"
                      onChange={(e) => {
                        e.stopPropagation();
                        selectAll();
                      }}
                      slotProps={{
                        input: {
                          id: `filter-${label.toLowerCase()}-all-checkbox`,
                          "aria-label": `${label} - All`,
                        },
                      }}
                      style={{
                        padding: "2px",
                        marginLeft: "6px",
                        color: "#fff",
                      }}
                    />
                  </button>
                )}
                {filteredOptions.map((option, idx) => (
                  <OptionRow
                    key={option}
                    option={option}
                    index={idx}
                    multiple={multiple}
                    label={label}
                    isSelected={
                      multiple
                        ? Array.isArray(value) && value.includes(option)
                        : value === option
                    }
                    onToggle={handleOptionClick}
                    showRadio={showRadio}
                  />
                ))}
                {isLoadingFromHook || isLoading ? (
                  <output
                    aria-live="polite"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "6px 0",
                    }}
                  >
                    <AccessibleSpinner
                      size={16}
                      label="Loading options"
                      sx={{ color: "#fff" }}
                    />
                  </output>
                ) : (
                  options.length === 0 && (
                    <output aria-live="polite" className="no-options">
                      No options available
                    </output>
                  )
                )}
              </div>
            </section>,
            document.body,
          )}
      </div>
    </div>
  );
};

FilterSelect.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  multiple: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  onOpen: PropTypes.func,
  onLoadMore: PropTypes.func,
  isLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  filterType: PropTypes.string,
  autoPrefetch: PropTypes.bool,
  showLabel: PropTypes.bool,
  showRadio: PropTypes.bool,
  instanceId: PropTypes.string,
};

const FilterText = ({
  label,
  placeholder,
  value,
  onChange,
  showLabel = true,
  instanceId,
}) => {
  const uidHook = useId();
  const uid = instanceId || uidHook;
  const idBase = `filter-${label.toLowerCase()}-${uid}`;
  const inputId = `${idBase}-text-input`;
  return (
    <div className="common-filter-input">
      {showLabel && (
        <label className="filter-label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <div className="filter-input-container">
        <input
          type="text"
          className="filter-input"
          placeholder={placeholder}
          value={value}
          id={inputId}
          onChange={(e) => onChange?.(e.target.value)}
          style={{ padding: "8px 12px" }}
        />
        <svg
          className="dropdown-icon"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </div>
    </div>
  );
};

FilterText.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  showLabel: PropTypes.bool,
  instanceId: PropTypes.string,
};

const CommonFilterInput = (props) => {
  const { type = "text" } = props;

  if (type === "select") {
    return <FilterSelect {...props} multiple={false} />;
  }

  if (type === "radio-select") {
    return <FilterSelect {...props} multiple={false} showRadio />;
  }

  return <FilterText {...props} />;
};

export default CommonFilterInput;

CommonFilterInput.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.oneOf(["text", "select", "radio-select"]),
  multiple: PropTypes.bool,
  onSearch: PropTypes.func,
  onOpen: PropTypes.func,
  onLoadMore: PropTypes.func,
  isLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  filterType: PropTypes.string,
  autoPrefetch: PropTypes.bool,
  showLabel: PropTypes.bool,

  showRadio: PropTypes.bool,

  instanceId: PropTypes.string,
};
